import { useMemo } from 'react';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
} from '@tanstack/react-table';
import { useTranslation } from '@/lib/i18n';
import type { ReleaseScheduleListItem } from '@/lib/api-admin/types';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui';
import { ReleaseStatusPill } from './ReleaseStatusPill';

export interface ReleaseSchedulesTableProps {
  items: ReadonlyArray<ReleaseScheduleListItem>;
  onEdit: (item: ReleaseScheduleListItem) => void;
  onCancel: (item: ReleaseScheduleListItem) => void;
  canWrite: boolean;
}

const formatDateTime = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('ko-KR');
};

const formatDate = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('ko-KR');
};

export const ReleaseSchedulesTable = ({
  items,
  onEdit,
  onCancel,
  canWrite,
}: ReleaseSchedulesTableProps) => {
  const { t } = useTranslation('settlement');

  const columns = useMemo<ColumnDef<ReleaseScheduleListItem>[]>(
    () => [
      {
        id: 'title',
        accessorKey: 'title',
        header: () => t('settlement.release.columns.title'),
        cell: ({ row }) => (
          <div className="flex flex-col">
            <span className="text-sm font-medium text-[color:var(--content-strong)]">
              {row.original.title}
            </span>
            {row.original.daysUntilRelease !== undefined && row.original.daysUntilRelease > 0 ? (
              <span className="text-[10px] text-[color:var(--content-muted)]">
                D-{row.original.daysUntilRelease}
              </span>
            ) : null}
          </div>
        ),
      },
      {
        id: 'vendor',
        accessorFn: (row) => row.vendorName ?? row.vendorId,
        header: () => t('settlement.release.columns.vendor'),
      },
      {
        id: 'artist',
        accessorFn: (row) => row.artistName ?? '—',
        header: () => t('settlement.release.columns.artist'),
      },
      {
        id: 'releaseAt',
        accessorFn: (row) => formatDateTime(row.releaseAt),
        header: () => t('settlement.release.columns.releaseAt'),
      },
      {
        id: 'status',
        header: () => t('settlement.release.columns.status'),
        cell: ({ row }) => (
          <ReleaseStatusPill
            status={row.original.status}
            scheduleId={row.original.scheduleId}
          />
        ),
      },
      {
        id: 'source',
        header: () => t('settlement.release.columns.source'),
        cell: ({ row }) => (
          <span
            className="rounded px-1.5 py-0.5 text-[10px]"
            style={{
              background:
                row.original.source === 'FUNDING'
                  ? 'var(--status-info-bg)'
                  : 'var(--status-neutral-bg)',
              color:
                row.original.source === 'FUNDING'
                  ? 'var(--status-info-fg)'
                  : 'var(--status-neutral-fg)',
            }}
            data-testid="release-source-badge"
            data-source={row.original.source}
          >
            {t(`settlement.release.source.${row.original.source}`, { defaultValue: '-' })}
          </span>
        ),
      },
      {
        id: 'createdAt',
        accessorFn: (row) => formatDate(row.createdAt),
        header: () => t('settlement.release.columns.createdAt'),
      },
      ...(canWrite
        ? [
            {
              id: 'actions',
              header: () => '',
              cell: ({ row }) => {
                const status = row.original.status;
                const isCancelled = status === 'CANCELLED';
                return (
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      className="rounded-md border px-2 py-1 text-xs focus-visible:outline-none focus-visible:ring-2"
                      style={{
                        borderColor: 'var(--divider-strong)',
                        color: 'var(--content-default)',
                      }}
                      onClick={() => onEdit(row.original)}
                      disabled={isCancelled}
                      data-testid="release-edit-btn"
                    >
                      {t('settlement.actions.editSchedule')}
                    </button>
                    <button
                      type="button"
                      className="rounded-md px-2 py-1 text-xs focus-visible:outline-none focus-visible:ring-2"
                      style={{
                        background: 'var(--status-danger-bg)',
                        color: 'var(--status-danger-fg)',
                      }}
                      onClick={() => onCancel(row.original)}
                      disabled={isCancelled}
                      data-testid="release-cancel-btn"
                    >
                      {t('settlement.actions.cancelSchedule')}
                    </button>
                  </div>
                );
              },
            } as ColumnDef<ReleaseScheduleListItem>,
          ]
        : []),
    ],
    [t, onEdit, onCancel, canWrite],
  );

  const table = useReactTable({
    data: items as ReleaseScheduleListItem[],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (items.length === 0) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="flex flex-col items-center gap-2 rounded-lg py-12 text-center text-sm"
        style={{
          background: 'var(--surface-raised)',
          color: 'var(--content-muted)',
          boxShadow: 'var(--shadow-card)',
        }}
      >
        <p>{t('settlement.release.empty')}</p>
      </div>
    );
  }

  return (
    <div
      className="overflow-hidden rounded-lg"
      style={{ background: 'var(--surface-raised)', boxShadow: 'var(--shadow-card)' }}
    >
      <Table data-testid="release-schedules-table">
        <TableHeader style={{ background: 'var(--surface-1)' }}>
          {table.getHeaderGroups().map((group) => (
            <TableRow key={group.id}>
              {group.headers.map((header) => (
                <TableHead key={header.id} scope="col">
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow
              key={row.id}
              data-testid="release-row"
              style={{ height: 'var(--density-compact-row-h)' }}
            >
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id}>
                  {flexRender(
                    cell.column.columnDef.cell ?? ((ctx) => ctx.getValue() as string),
                    cell.getContext(),
                  )}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
